import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getUnreadMentions } from '../../services/mention.service';
import SearchOverlay from './SearchOverlay';
import api from '../../services/api';

// Which part of My Hub a page belongs to — counted (section only, nothing
// the member wrote or did) so Team365 can see which parts members use
// (Sales & Marketing To-Do 11, 2026-09-29).
const HUB_SECTIONS = [
  ['/dashboard/continuums', 'Continuums'], ['/dashboard/events', 'Events'], ['/dashboard/replays', 'Events'],
  ['/dashboard/discussion', 'Hive'], ['/dashboard/announcements', 'Hive'], ['/dashboard/learning-lab', 'Learning'],
  ['/dashboard/planning', 'Plan'], ['/dashboard/quarterly-checkin', 'Compass'],
];
const hubSectionFor = (path) => {
  if (path === '/dashboard' || path === '/dashboard/') return 'Dashboard';
  const hit = HUB_SECTIONS.find(([prefix]) => path.startsWith(prefix));
  return hit ? hit[1] : null;
};

export const NAV_ITEMS = [
  { to: '/dashboard',              label: 'Dashboard',     iconImg: '/myhub-logo-icon.png' },
  { to: '/dashboard/continuums',   label: 'My Continuums', icon: '🎯' },
  { to: '/dashboard/events',       label: 'My Events',     iconImg: '/events-icon.png' },
  { to: '/dashboard/discussion',   label: 'My Hive',       iconImg: '/hive-icon.png' },
  { to: '/dashboard/learning-lab', label: 'My Learning',   iconImg: '/learning-lab-icon.png' },
  { to: '/dashboard/planning',     label: 'My Plan',       icon: '📋' },
  { to: '/dashboard/settings',     label: 'Settings',      icon: '⚙️'  },
  { to: '/dashboard/integrations', label: 'Devices',       icon: '📲' },
];

/** Superset of NAV_ITEMS for page-title icon lookups — includes pages that
 * exist but aren't unconditional nav-row items (My Payouts is
 * affiliate-only; Tell Us/Book a Call live in the sidebar/top-menu, not
 * the icon rows). Single source of truth so the nav and every page's <h1>
 * always agree — Jenn, 2026-08-27: "it should be consistent across the
 * pages." */
const PAGE_ICONS = [
  ...NAV_ITEMS,
  { to: '/dashboard/affiliate',   label: 'My Payouts', icon: '💰' },
  { to: '/dashboard/tell-us',     label: 'Tell Us',     icon: '💬' },
  { to: '/dashboard/book-a-call', label: 'Book a Call', icon: '📞' },
  { to: '/admin',                 label: 'Team365',     icon: '🛠️' },
];

export const getNavIcon = (path) => PAGE_ICONS.find(i => i.to === path) || {};

/** Renders an item's icon — a branded image if provided, else the emoji fallback. */
function NavIcon({ item, className }) {
  if (item.iconImg) return <img src={item.iconImg} alt="" className={`${className} object-contain`} />;
  return <span className={className}>{item.icon}</span>;
}

/**
 * Shell layout for all dashboard pages.
 * Desktop: fixed left sidebar + main content area.
 * Mobile:  sticky bottom nav + scrollable content.
 */
// Jenn, 2026-10-26: "open up other pages in 365reset while I am working in one page... like a pane. When I need to
// grab a video id, I have to get out of the page I'm working on and copy it, then go back." The side panel shows any
// 365reset page beside the one she's on. A page shown inside the panel drops the sidebar and top bars.
const IN_PANEL = typeof window !== 'undefined' && window.self !== window.top;
const PANEL_PAGES = [
  ['/admin/bunny-library', 'Video Library', true], ['/admin/courses', 'Courses', true], ['/admin/hive-library', 'Hive Library', true],
  ['/admin/todos', 'To-Dos', true], ['/admin/members', 'Members', true], ['/admin/hub-events', 'Events (Team365)', true],
  ['/admin/sales-marketing', 'Sales & Marketing', true], ['/admin/backend', 'BackEnd', true],
  ['/dashboard', 'My Dashboard', false], ['/dashboard/learning-lab', 'My Learning', false], ['/dashboard/events', 'My Events', false],
  ['/dashboard/discussion', 'My Hive', false], ['/dashboard/planning', 'My Plan', false],
];
const PANEL_W = 'min(46vw, 640px)';

function SidePanel({ isAdmin, onClose }) {
  const pages = PANEL_PAGES.filter(([, , adminOnly]) => isAdmin || !adminOnly);
  const [src, setSrc] = useState(() => { try { return sessionStorage.getItem('side-panel-page') || pages[0][0]; } catch { return pages[0][0]; } });
  const frame = useRef(null);
  const navigate = useNavigate();
  const choose = (to) => { setSrc(to); try { sessionStorage.setItem('side-panel-page', to); } catch { /* private mode */ } };
  const openFull = () => {
    let here = src;
    try { const l = frame.current.contentWindow.location; here = l.pathname + l.search + l.hash; } catch { /* keep src */ }
    onClose(); navigate(here);
  };
  return (
    <div className="hidden md:flex flex-col fixed top-0 right-0 bottom-0 z-40 bg-reset-cream border-l border-reset-creamDark shadow-xl" style={{ width: PANEL_W }} role="complementary" aria-label="Side panel">
      <div className="flex items-center gap-2 px-3 py-2 bg-reset-brown text-white">
        <span className="text-sm font-semibold">Side panel</span>
        <select value={pages.some(([to]) => to === src) ? src : ''} onChange={e => e.target.value && choose(e.target.value)}
          className="text-sm text-reset-brown rounded-md px-2 py-1 flex-1 min-w-0" aria-label="Page to show in the side panel">
          {!pages.some(([to]) => to === src) && <option value="">(another page)</option>}
          {pages.map(([to, label]) => <option key={to} value={to}>{label}</option>)}
        </select>
        <button type="button" onClick={() => { try { frame.current.contentWindow.location.reload(); } catch { /* ignore */ } }} className="text-xs bg-white/15 rounded-md px-2 py-1" aria-label="Refresh the side panel">↻</button>
        <button type="button" onClick={openFull} className="text-xs bg-white/15 rounded-md px-2 py-1">Open full page</button>
        <button type="button" onClick={onClose} className="text-lg leading-none px-1" aria-label="Close the side panel">×</button>
      </div>
      <iframe ref={frame} key={src} src={src} title="Side panel page" allow="clipboard-read; clipboard-write" className="flex-1 w-full border-0 bg-reset-cream" />
    </div>
  );
}

export default function DashboardLayout({ children, wide = false }) {
  const { user, logout, isAdmin } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(() => { try { return sessionStorage.getItem('side-panel-open') === '1'; } catch { return false; } });
  const togglePanel = (open) => { setPanelOpen(open); try { sessionStorage.setItem('side-panel-open', open ? '1' : '0'); } catch { /* private mode */ } };
  const navigate         = useNavigate();
  const location          = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // One visit per page opened — fire-and-forget, never in the way.
  useEffect(() => {
    const section = hubSectionFor(location.pathname);
    if (section && user) api.post('/members/me/engagement', { type: 'hub_page', ref: section }).catch(() => {});
    // Every page, with its address (Jenn, 2026-10-08) — Team365 → Members → "Who visited a page".
    if (user) api.post('/members/me/engagement', { type: 'page_view', ref: location.pathname }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const [hiveUnread, setHiveUnread] = useState(0);

  // The nav badge is deliberately mention-count only, not general "new
  // activity in Hive" — Jenn, 2026-09-01: "We don't need a note about Hive
  // activity. We only need to be flagged if someone tags us." A comment or
  // post existing is visible to everyone regardless; this badge is only
  // for "someone specifically tagged you," matching MentionsBar's own
  // per-member-scoped count. Clears only when a mention is actually marked
  // read (clicking it), not just by visiting the Hive page.
  useEffect(() => {
    let cancelled = false;
    getUnreadMentions().then(({ count }) => { if (!cancelled) setHiveUnread(count); }).catch(() => {});
    return () => { cancelled = true; };
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/auth/login');
  };

  const firstName = (user?.name || '').split(' ')[0] || 'there';
  // Paused members see only Settings (Jenn, 2026-10-07); ProtectedRoute
  // sends any other page back there.
  const paused = !isAdmin && !!user?.pause_ends_at && new Date(user.pause_ends_at) > new Date();
  const navItems = paused ? NAV_ITEMS.filter(i => i.to === '/dashboard/settings') : [
    ...NAV_ITEMS,
    // Only affiliates/royalty partners see this — most members never will.
    ...(user?.is_affiliate_partner ? [{ to: '/dashboard/affiliate', label: 'My Payouts', icon: '💰' }] : []),
    ...(isAdmin ? [{ to: '/admin', label: 'Team365', icon: '🛠️' }] : []),
  ];

  if (IN_PANEL) {
    return <div className="min-h-dvh bg-reset-cream"><div className="px-3 py-4">{children}</div></div>;
  }

  return (
    <div className="min-h-dvh bg-reset-cream flex">

      {/* ── Desktop sidebar ─────────────────────────────────────── */}
      <aside className="hidden md:flex flex-col w-60 bg-reset-brown text-white fixed inset-y-0 left-0 z-20">

        {/* Logo */}
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="365reset" className="h-7 w-7 object-contain" />
            <span className="font-serif text-lg font-bold tracking-wide text-white">365reset</span>
          </div>
          <p className="text-xs text-white/50 mt-0.5">Dashboard</p>
        </div>

        {/* Member greeting */}
        <div className="px-6 py-4 border-b border-white/10">
          <p className="text-xs text-white/50 uppercase tracking-wider">Logged in as</p>
          <p className="text-sm font-medium text-white mt-0.5 truncate">{firstName}</p>
        </div>

        {/* Nav links — min-h-0 is required alongside flex-1 here: without it,
            a flex child sizes to its content and pushes past the fixed-height
            aside instead of shrinking, which is exactly what made this
            unscrollable (Jenn/Patti, 2026-08-31 — Settings/Devices were
            cut off with no way to reach them on a shorter viewport). */}
        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/dashboard'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                ${isActive
                  ? 'bg-reset-terracotta text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'}`
              }
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-md bg-reset-creamDark shrink-0">
                <NavIcon item={item} className="w-4 h-4" />
              </span>
              {item.label}
              {item.to === '/dashboard/discussion' && hiveUnread > 0 && (
                <span className="ml-auto min-w-[1.25rem] h-5 px-1 rounded-full bg-reset-terracotta text-white text-[11px] font-bold flex items-center justify-center">
                  {hiveUnread > 9 ? '9+' : hiveUnread}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Tell Us + Book a call */}
        {!paused && <div className="px-3 pb-2 space-y-1">
          <NavLink
            to="/dashboard/tell-us"
            className={({ isActive }) =>
              `flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
              ${isActive ? 'bg-reset-terracotta text-white' : 'text-white/70 hover:text-white hover:bg-white/10'}`
            }
          >
            <span className="w-7 h-7 flex items-center justify-center rounded-md bg-reset-creamDark shrink-0 text-sm">💬</span> Tell Us
          </NavLink>
          <NavLink
            to="/dashboard/book-a-call"
            className={({ isActive }) =>
              `flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
              ${isActive ? 'bg-reset-terracotta text-white' : 'text-white/70 hover:text-white hover:bg-white/10'}`
            }
          >
            <span className="w-7 h-7 flex items-center justify-center rounded-md bg-reset-creamDark shrink-0 text-sm">📞</span> Book a Call with Jenn
          </NavLink>
        </div>}

        {/* Logout */}
        <div className="px-3 py-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          >
            <span className="w-7 h-7 flex items-center justify-center rounded-md bg-reset-creamDark shrink-0 text-sm">🚪</span> Sign out
          </button>
        </div>
      </aside>

      {/* Jenn, 2026-10-26: "have a refresh button visible at every page." The iPad home-screen app has
          no browser reload, so this stays fixed in the top-right corner of every page, even while scrolling.
          A reload keeps the same page and #tab, and picks up the newest version of the app. */}
      <div className="hidden md:flex fixed top-3 z-50 gap-2" style={{ right: panelOpen ? `calc(${PANEL_W} + 12px)` : '16px' }}>
        {/* Jenn, 2026-10-29: "Where is the search key in Reset? We need one." */}
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="flex items-center gap-1 rounded-full bg-white text-reset-brown border border-reset-creamDark text-sm font-semibold px-4 py-2 shadow-md hover:border-reset-terracotta focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        >
          🔍 Search
        </button>
        <button
          type="button"
          onClick={() => togglePanel(!panelOpen)}
          className="flex items-center gap-1 rounded-full bg-reset-brown text-white text-sm font-semibold px-4 py-2 shadow-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          aria-pressed={panelOpen}
        >
          {panelOpen ? '⧉ Close panel' : '⧉ Side panel'}
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="flex items-center gap-1 rounded-full bg-reset-terracotta text-white text-sm font-semibold px-4 py-2 shadow-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-reset-brown"
          aria-label="Refresh this page"
        >
          ↻ Refresh
        </button>
      </div>
      {panelOpen && <SidePanel isAdmin={isAdmin} onClose={() => togglePanel(false)} />}
      {searchOpen && <SearchOverlay isAdmin={isAdmin} onClose={() => setSearchOpen(false)} />}

      {/* ── Main content ─────────────────────────────────────────── */}
      <main className="flex-1 min-w-0 md:ml-60 pb-20 md:pb-0" style={panelOpen && window.innerWidth >= 768 ? { marginRight: PANEL_W } : undefined}>

        {/* Mobile top bar — Book a Call / Sign out live in the menu here
            instead of the bottom icon row, per Jenn, 2026-08-27: "I'm fine
            with the sign out and book a call even being a drop down menu
            from 365reset at the top (hamburger?)." */}
        <div className="md:hidden bg-reset-brown text-white flex items-center justify-between px-4 py-3 sticky top-0 z-30 relative">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="365reset" className="h-6 w-6 object-contain" />
            <span className="font-serif font-bold text-sm tracking-wide">365reset</span>
            <button type="button" onClick={() => setSearchOpen(true)} className="ml-2 rounded-full bg-white text-reset-brown text-xs font-semibold px-3 py-1" aria-label="Search">🔍</button>
            <button type="button" onClick={() => window.location.reload()} className="ml-2 rounded-full bg-reset-terracotta text-white text-xs font-semibold px-3 py-1" aria-label="Refresh this page">↻ Refresh</button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/70">{firstName}</span>
            <button
              onClick={() => setMenuOpen(v => !v)}
              aria-label="More options"
              className="w-8 h-8 flex items-center justify-center rounded-md bg-white/10 hover:bg-white/20 transition-colors"
            >
              <span className="text-base leading-none">⋯</span>
            </button>
          </div>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-4 top-full mt-1 w-56 bg-reset-cream rounded-xl shadow-lg overflow-hidden z-30 text-reset-brown">
                {!paused && <Link
                  to="/dashboard/book-a-call"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-sm font-medium hover:bg-reset-creamDark transition-colors"
                >
                  <span className="text-base">📞</span> Book a Call with Jenn
                </Link>}
                {/* Jenn, 2026-09-29: saved to the iPhone home screen, the app has
                    no address bar or reload button, and pull-down-to-refresh
                    doesn't work there — "put a refresh option in ... where we
                    have the sign out stuff in that pull down menu." */}
                <button
                  onClick={() => { setMenuOpen(false); window.location.reload(); }}
                  className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium hover:bg-reset-creamDark transition-colors border-t border-reset-creamDark"
                >
                  <span className="text-base">🔄</span> Refresh
                </button>
                <button
                  onClick={() => { setMenuOpen(false); handleLogout(); }}
                  className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium hover:bg-reset-creamDark transition-colors border-t border-reset-creamDark"
                >
                  <span className="text-base">🚪</span> Sign out
                </button>
              </div>
            </>
          )}
        </div>

        {/* Page content (wide: the three-column member page, 2026-10-28) */}
        <div className={`${wide ? 'max-w-[1400px]' : 'max-w-5xl'} mx-auto px-4 md:px-8 py-6 md:py-8`}>
          {children}
        </div>
      </main>

      {/* ── Mobile bottom nav ─────────────────────────────────────
          Icon-only by default — with this many items (up to 10 for an
          admin/affiliate) a label on every one crammed the row unreadably.
          Book a Call / Sign out moved to the top-bar menu instead, since
          they're account actions, not navigation. Each icon sits on a
          solid cream chip — several of the branded icons (Events, Learning)
          are thin light line-art meant for a light background, and were
          nearly invisible against the dark bar with just a faint white
          tint. Active state shown via a terracotta ring around the chip
          rather than a color fill, so it never fights the icon's own
          colors. Label shown as a hover/focus tooltip.
          Jenn, 2026-08-27: "default to use its icon, not its name... the
          name could be viewed when hovered over" / "some icons don't show
          up enough."
          Chips shrink to fit (rather than the row scrolling) specifically
          so the tooltip never gets clipped — overflow-x-auto on this row
          would force overflow-y to auto too (a real CSS rule: an element
          can't scroll on one axis while staying visible on the other), and
          that silently cut the tooltip off before it could pop up above
          the bar. Jenn caught it: "the text pop up in phone is trying to
          show, but fails." */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-reset-brown border-t border-white/10 flex z-20">
        {navItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/dashboard'}
            className="group relative flex-1 min-w-0 flex flex-col items-center justify-center py-2"
          >
            {({ isActive }) => (
              <>
                <span className={`relative flex items-center justify-center w-full max-w-[44px] aspect-square rounded-xl bg-reset-creamDark transition-shadow ${isActive ? 'ring-2 ring-reset-terracotta' : ''}`}>
                  <NavIcon item={item} className="w-5 h-5" />
                  {item.to === '/dashboard/discussion' && hiveUnread > 0 && (
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-reset-terracotta border border-reset-brown" />
                  )}
                </span>
                <span className="pointer-events-none absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 whitespace-nowrap bg-reset-brownDeep text-white text-[11px] font-medium px-2 py-1 rounded-md shadow-lg opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity z-30">
                  {item.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

    </div>
  );
}
